<template>
    <v-form ref="form">
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="3" class="pe-1">
                <v-text-field
                    label="Code"
                    outlined
                    dense
                    hide-details="auto"
                    background-color="disabled"
                    disabled
                    :value="form.code"
                >
                </v-text-field>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="2" class="pe-1">
                <v-text-field
                    outlined
                    dense
                    label="Reference"
                    v-model="form.reference_id"
                    hide-details="auto"
                >
                </v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pe-1" v-if="form.purchase_order_id && form.purchase_order_code">
                <v-text-field
                    label="Order Code"
                    outlined
                    dense
                    hide-details="auto"
                    background-color="disabled"
                    disabled
                    :value="form.purchase_order_code"
                >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="3" class="pe-1">
                <PDatePicker
                    label="Date"
                    :rules="[isRequired]"
                    v-model="form.date"
                    hide
                    dense
                    outlined
                >
                </PDatePicker>
            </v-col>
            <v-col cols="12" md="3" class="pe-1">
                <PDatePicker
                    label="Due Date"
                    v-model="form.due_date"
                    :rules="[isRequired]"
                    hide
                    dense
                    outlined
                >
                </PDatePicker>
            </v-col>
        </v-row>
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="4" class="pe-1">
                <v-autocomplete
                    label="Supplier"
                    :rules="[isRequired]"
                    :items="suppliers"
                    @update:search-input="searchSuppliers"
                    @change="handleSupplierChange"
                    item-text="display_name"
                    v-model="form.supplier_id"
                    item-value="id"
                    outlined
                    hide-details="auto"
                    dense
                >
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="pe-1">
                <v-autocomplete
                    label="Account"
                    :rules="[isRequired]"
                    :items="supplierAccounts"
                    item-text="display_name"
                    item-value="id"
                    v-model="form.account_id"
                    outlined
                    hide-details="auto"
                    dense
                >
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="2" class="pe-1">
                <v-text-field
                    v-show="form.currency_id"
                    label="Currency"
                    outlined
                    disabled
                    :value="currency ? currency.abbreviation : null"
                    background-color="disabled"
                    hide-details="auto"
                    dense
                    >
                </v-text-field>
            </v-col>
        </v-row>
        <v-divider class="mb-2"></v-divider>
        <PurchaseInvoiceFormLine
            v-for="(line, index) in form.lines"
            :key="line.key"
            v-model="form.lines[index]"
            @delete:line="handleDeleteLine(index)"
        />
        <v-btn block depressed small @click="addLine">
            <v-icon left>mdi-playlist-plus</v-icon>
            add line
        </v-btn>
        <v-divider class="my-2"></v-divider>
        <v-row no-gutters>
            <v-col cols="12" md="8" class="pe-2">
                <v-textarea outlined label="Remarks" hide-details="auto"></v-textarea>
            </v-col>
            <v-col cols="12" md="4">
                <v-sheet outlined>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Subtotal</th>
                                <td>{{currency ? currency.abbreviation : null}} {{formatNumber(getTotal(form.lines, "subtotal"))}}</td>
                            </tr>
                            <tr>
                                <th>Tax</th>
                                <td>{{currency ? currency.abbreviation : null}} {{formatNumber(getTotal(form.lines, "tax"))}}</td>
                            </tr>
                            <tr>
                                <th>Total</th>
                                <td>{{currency ? currency.abbreviation : null}} {{formatNumber(amount)}} <span class="grey--text"> @ {{formatNumber(this.form.exchange_rate)}} </span></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-sheet>
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import { formatNumber } from 'accounting';
import { getTotal } from './../../utils/accounting';
import { mapActions, mapGetters, mapState } from 'vuex';
import PurchaseInvoiceFormLine from "./../../components/purchase/PurchaseInvoiceFormLine.vue";
import PDatePicker from "./../common/PDatePicker";
import { isRequired } from "./../../utils/validations";
import { randomSix } from '../../utils/toolkit';

export default {
    name: "PurchaseInvoiceForm",
    components: {
        PDatePicker,
        PurchaseInvoiceFormLine,
    },
    props: {
        value: Object,
        editMode: Boolean
    },
    data() {
        return {
            form: {
                code: null,
                date: null,
                due_date: null,
                supplier_id: null,
                reference_id: null,
                account_id: null,
                currency_id: null,
                amount: 0,
                base_amount: 0,
                lines: [],
                remarks: ""
            },
            line: {
                item_id: null,
                measure_unit_id: null,
                quantity: null,
                price: null,
                subtotal: null,
                tax_id: 0,
                tax: 0,
                total: 0,
            },
        };
    },
    watch: {
        form: {
            deep: true,
            handler(form) {
                this.$emit("input", {...form, lines: [...form.lines]});
            }
        },
        currency(value) {
            if(!value) return
            this.form.currency_id = value.id;
        },
        amount(value) {
            this.form.amount = value;
        },
        baseAmount(value) {
            this.form.base_amount = value;
        }
    },
    computed: {
        ...mapState("accounting", ["baseCurrency"]),
        ...mapGetters({
            currencies: "purchase/currency/list",
            suppliers: "purchase/supplier/list",
        }),
        supplier() {
            return this.suppliers.find(supplier => supplier.id == this.form.supplier_id);
        },
        supplierAccounts() {
            if(!this.supplier) return [];
            if(!Array.isArray(this.supplier.accounts)) return [];
            return this.supplier.accounts;
        },
        supplierAccount() {
            return this.supplierAccounts.find(account => account.id == this.form.account_id);
        },
        currency() {
            if(!this.supplierAccount) return null;
            return this.supplierAccount.currency;
        },
        amount() {
            return getTotal(this.form.lines, "total");
        },
        baseAmount() {
            if(!this.currency) return 0;
            return this.amount * this.form.exchange_rate;
        }
    },
    methods: {
        isRequired, getTotal, 
        formatNumber(n){
            return formatNumber(n, 2);
        },
        addLine() {
            this.form.lines.push({ ...this.line, key: randomSix() });
        },
        handleDeleteLine(index){
            this.form.lines.splice(index, 1)
        },
        ...mapActions({
            searchSuppliers: "purchase/supplier/search",
            fetchCurrencies: "purchase/currency/search",
            fetchTaxRates: "purchase/tax/index",
        }),
        validate() {
            return this.$refs.form.validate()
        },
        handleSupplierChange() {
            this.form.account_id = null;
        },
        deepCloneValue(value) {
            let clone = {amount: 0, base_amount: 0, ...value};
            if(clone.lines && clone.lines.length > 0) {
                clone = {...clone, lines: [...clone.lines]};
            }
            return clone;
        },
    },
    mounted() {
        this.fetchCurrencies()
        this.fetchTaxRates()
        if(this.value) {
            this.form = this.deepCloneValue(this.value);
        }

    }
};
</script>

<style>
</style>